import { formatLocalDateTime, localDateInputValue } from '../utils/localDateTime';
import React, { useState, useRef, useCallback, useEffect } from 'react';

// ─── Types ─────────────────────────────────────────────────────────────────────
export interface AgreementRecord {
    version: string;
    agreementHash: string;
    tier: string;
    acknowledgedAt: string;
    scrollCompletedAt: string;
    signatureName: string;
    acknowledgedByEmail: string;
    acknowledgedByUid: string;
    checkboxes: Record<string, boolean>;
    pdfDownloaded: boolean;
    userAgent: string;
    timezone: string;
    planLabel: string;
}

interface SubscriberAgreementModalProps {
    tier: 'individual' | 'business';
    userEmail: string;
    userUid: string;
    billingLabel: string; // e.g. "Individual Professional — $99/month"
    onAccepted: (record: AgreementRecord) => void;
    onDeclined: () => void;
}

// ─── Legal Text ────────────────────────────────────────────────────────────────
const AGREEMENT_VERSION = 'v2.2-PA-2026-04';

const EFFECTIVE_DATE = 'April 15, 2026';
const COMPANY_NAME = 'IntelOptics Insights';
const COMPANY_LEGAL = 'Eprims Ltd d/b/a IntelOptics Insights';
const CONTACT_EMAIL = 'contact@eprims.com';
const GOVERNING_STATE = 'Commonwealth of Pennsylvania';
const GOVERNING_VENUE = 'Courts of Delaware County, Pennsylvania';

function buildAgreementText(tier: 'individual' | 'business', planLabel: string): string {
    const tierLabel = tier === 'business' ? 'Business Intelligence' : 'Individual Professional';
    return `
INTELOPTICS INSIGHTS
SUBSCRIBER AGREEMENT & POLICIES
Effective Date: ${EFFECTIVE_DATE} | Version: ${AGREEMENT_VERSION}
Plan: ${planLabel}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PART 1 — SUBSCRIBER AGREEMENT (${tierLabel.toUpperCase()})
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This Subscriber Agreement ("Agreement") is a legally binding contract between you ("Subscriber") and ${COMPANY_LEGAL}, a company operating under the laws of the ${GOVERNING_STATE} ("Company"). By subscribing, you confirm you have the legal authority to enter into this Agreement.

ARTICLE 1 — GRANT OF LICENSE
Company grants Subscriber a limited, non-exclusive, non-transferable, non-sublicensable right to access and use the IntelOptics Insights platform (the "Service") during the Subscription Term, solely for internal professional purposes consistent with the ${tierLabel} tier feature set. No rights are granted beyond those expressly stated herein.

ARTICLE 2 — SUBSCRIPTION FEES & BILLING
2.1 FEES. Subscriber shall pay all fees specified for the ${tierLabel} tier. Current pricing is displayed at checkout and subject to change with 30 days' notice.
2.2 PAYMENT PROCESSOR. All payments are processed by Paddle.com Market Ltd ("Paddle"), acting as the Merchant of Record. By subscribing, you agree to Paddle's Terms of Service and Privacy Policy.
2.3 TAXES. All fees are exclusive of applicable federal, state, and local taxes, including but not limited to Pennsylvania Sales Tax where applicable. Subscriber is responsible for all such taxes.
2.4 AUTO-RENEWAL. Subscriptions auto-renew at the end of each billing cycle. You may cancel through Billing & Invoices to prevent future charges.
${tier === 'business' ? `2.5 SEAT LICENSES. Additional Admin Seats ($55/seat/month) and User Seats ($35/seat/month) are billed in addition to the base subscription fee and are non-refundable once provisioned.\n` : ''}
ARTICLE 3 — INTELLECTUAL PROPERTY & PROPRIETARY RIGHTS
3.1 PLATFORM OWNERSHIP. The Service, including all software, AI models, proprietary "Insights Engines," intelligence feed architectures, prompt engineering methodologies, "Build-to-Audit" bridge mechanisms, PMESII-PT analytical frameworks, workflows, and all derived technologies, is the exclusive intellectual property of ${COMPANY_LEGAL} and its licensors, protected by U.S. Copyright law (17 U.S.C. § 1 et seq.), trade secret law, and applicable international agreements.
3.2 SUBSCRIBER CONTENT. You retain all rights to text, data, and content you provide ("Inputs"). Subject to compliance with this Agreement, Company assigns to you all right, title, and interest in Insights-generated reports and analyses ("Outputs") produced from your Inputs.
3.3 RESTRICTIONS. Subscriber is strictly prohibited from: (a) reverse-engineering, decompiling, or disassembling any portion of the Service; (b) publishing, sharing, or selling the platform's internal methodologies, prompt architectures, or processing logic; (c) using Outputs to train competing AI systems; (d) reselling or sublicensing access to the Service without written consent; (e) creating derivative works based on the platform's proprietary frameworks.
3.4 FEEDBACK. Any suggestions, ideas, or feedback you provide regarding the Service may be used by Company without restriction or compensation.

ARTICLE 4 — NON-DISCLOSURE AGREEMENT (NDA)
Subscriber expressly acknowledges that the internal architecture, analytical frameworks, AI guidance notes ("Senior Analyst" prompts), and processing logic of the Service constitute trade secrets of ${COMPANY_LEGAL}. Subscriber shall: (a) maintain strict confidentiality of all proprietary platform information; (b) not disclose such information to any third party without prior written consent; (c) immediately notify Company of any known or suspected unauthorized disclosure. This NDA obligation survives termination of this Agreement for a period of five (5) years.

ARTICLE 5 — ACCEPTABLE USE POLICY (AUP)
Subscriber shall NOT use the Service to:
• Engage in, plan, or facilitate any illegal activity, including terrorism, human trafficking, or violence.
• Circumvent, disable, or attack any security feature or safety filter of the platform.
• Generate content promoting discrimination, harassment, or hate speech against any protected class.
• Process Classified, government-restricted, or military-sensitive information.
• Input Sensitive Personal Identifiable Information (SSNs, biometric data, neural data) or Protected Health Information (PHI) subject to HIPAA (45 C.F.R. § 164).
• Violate any U.S. export control law or access the Service from OFAC-sanctioned countries.
• Use the platform for autonomous weapons systems, autonomous medical diagnosis, or critical infrastructure control without robust Human-in-the-Loop (HITL) verification.
Company reserves the right to immediately suspend access for AUP violations without refund.

ARTICLE 6 — DISCLAIMERS & WARRANTIES
6.1 AS IS. THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." COMPANY MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
6.2 INSIGHTS ACCURACY. COMPANY DOES NOT WARRANT THAT INSIGHTS-GENERATED OUTPUTS ARE ACCURATE, COMPLETE, CURRENT, OR FREE FROM ERRORS OR BIAS. All Outputs are for informational and analytical support only. Subscriber accepts sole responsibility for verifying Outputs with qualified professionals before taking any operational action.
6.3 UPTIME. Company will use commercially reasonable efforts to maintain 99.9% uptime but does not guarantee uninterrupted access.

ARTICLE 7 — LIMITATION OF LIABILITY
TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ${COMPANY_LEGAL.toUpperCase()}, ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES (INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS OPPORTUNITY) ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE SERVICE, REGARDLESS OF THE CAUSE OF ACTION OR THE THEORY OF LIABILITY, EVEN IF COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
COMPANY'S TOTAL CUMULATIVE LIABILITY FOR ALL CLAIMS ARISING OUT OF THIS AGREEMENT SHALL NOT EXCEED THE TOTAL SUBSCRIPTION FEES PAID BY SUBSCRIBER IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO SUCH CLAIM.
Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability; in such cases, Company's liability is limited to the fullest extent permitted by law.

ARTICLE 8 — INDEMNIFICATION
Subscriber shall defend, indemnify, and hold harmless ${COMPANY_LEGAL} and its affiliates from any claims, damages, losses, and expenses (including reasonable legal fees) arising out of: (a) Subscriber's use of the Service; (b) Subscriber's breach of this Agreement; (c) Subscriber's distribution or reliance upon Insights-generated Outputs; (d) any data Subscriber provides that infringes third-party rights.

ARTICLE 9 — TERMINATION & DATA RETENTION
9.1 CANCELLATION. Subscriber may cancel through Billing & Invoices. Access remains active through the end of the current billing period.
9.2 GRACE PERIOD. Upon cancellation, data is retained in an "Inactive" vault for 30 days. Re-subscribing within 30 days restores all data.
9.3 PERMANENT DELETION. After 30 days in Inactive status, all organization data is permanently purged from Company systems ("Deep-Clean"). This process is irreversible.
9.4 EFFECT OF TERMINATION. Upon termination, the license granted herein terminates immediately. NDA obligations (Article 4) survive termination.

ARTICLE 10 — GOVERNING LAW & DISPUTE RESOLUTION
10.1 GOVERNING LAW. This Agreement shall be governed by and construed in accordance with the laws of the ${GOVERNING_STATE}, without regard to its conflict-of-law principles.
10.2 VENUE. Any legal action or proceeding arising under this Agreement shall be brought exclusively in the ${GOVERNING_VENUE}. Subscriber hereby consents to personal jurisdiction in Pennsylvania.
10.3 ARBITRATION. EXCEPT FOR CLAIMS FOR INJUNCTIVE RELIEF, ANY DISPUTE ARISING OUT OF OR RELATING TO THIS AGREEMENT SHALL BE FINALLY RESOLVED BY BINDING ARBITRATION UNDER THE COMMERCIAL ARBITRATION RULES OF THE AMERICAN ARBITRATION ASSOCIATION (AAA), CONDUCTED IN THE ${GOVERNING_STATE}. THE ARBITRATOR'S DECISION SHALL BE FINAL AND BINDING AND MAY BE ENTERED IN ANY COURT OF COMPETENT JURISDICTION.
10.4 CLASS ACTION WAIVER. SUBSCRIBER WAIVES ANY RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION AGAINST COMPANY.
10.5 JURY TRIAL WAIVER. TO THE EXTENT PERMITTED BY LAW, SUBSCRIBER WAIVES ANY RIGHT TO A JURY TRIAL.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PART 2 — PRIVACY POLICY (PA & FEDERAL COMPLIANCE)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Effective Date: ${EFFECTIVE_DATE}

${COMPANY_NAME} ("Company," "we," "us") is committed to protecting your privacy. This Privacy Policy complies with applicable U.S. federal and state privacy laws, including the Breach of Personal Information Notification Act (73 P.S. § 2301 et seq.) of the ${GOVERNING_STATE}, the Federal Trade Commission Act (15 U.S.C. § 41 et seq.), the California Consumer Privacy Act / CPRA (Cal. Civ. Code § 1798.100), the Children's Online Privacy Protection Act (COPPA, 15 U.S.C. § 6501), and applicable U.S. state comprehensive privacy laws.

SECTION 1 — DATA WE COLLECT
1.1 Account Data: Name, email address, professional role, and organization name collected at registration.
1.2 Subscription & Billing Data: Transaction IDs, billing history (processed and stored by Paddle — we do not store full payment card data).
1.3 Usage Data: Platform interactions, workflow executions, session duration, feature usage analytics.
1.4 Query Inputs: Text queries and data you submit for Insights analysis ("Inputs"). Inputs are processed transiently and are NOT stored persistently on our servers.
1.5 Technical Data: IP address approximation (for legal compliance), browser type, operating system, and timezone.
1.6 Legal Agreement Records: Your signed agreement records, including digital signature, timestamp, and checkbox confirmations, are stored in your organization's Firestore profile for legal compliance purposes.

SECTION 2 — HOW WE USE DATA
2.1 Service Delivery: To provide, maintain, and improve the platform.
2.2 Billing & Subscription Management: To process payments via Paddle.
2.3 Legal Compliance: To maintain agreement records as required by the E-SIGN Act (15 U.S.C. § 7001) and applicable contract law.
2.4 Security & Fraud Prevention: To detect and prevent unauthorized access.
2.5 Communications: Product updates, security notices, and support responses.
WE DO NOT SELL, RENT, OR SHARE YOUR PERSONAL DATA FOR ADVERTISING PURPOSES.

SECTION 3 — INSIGHTS DATA PROCESSING
3.1 No Model Training: Your Inputs and resulting Outputs are NEVER used to train or improve underlying Insights models.
3.2 Stateless Processing: Inputs are transmitted via encrypted channels to Google's Gemini Enterprise API, processed in-memory, and not retained by Google for model improvement under our Enterprise Data Processing Agreement.
3.3 US-Region Processing: All data is processed within United States-based Google Cloud regions.
3.4 Restricted Data Processing: Our infrastructure operates in "Restricted Data Processing" mode — Google acts solely as a data processor, not a data controller.

SECTION 4 — STATE-SPECIFIC RIGHTS
4.1 Pennsylvania Residents: Under the PA Breach of Personal Information Notification Act (73 P.S. § 2301), we will notify you within required timeframes in the event of a data breach involving your personal information.
4.2 California Residents (CCPA/CPRA): You have the right to: (a) know what personal information is collected; (b) delete personal information; (c) correct inaccurate personal information; (d) opt-out of sale/sharing (we do not sell data); (e) non-discrimination for exercising rights. To exercise rights, contact ${CONTACT_EMAIL}.
4.3 All US Residents: We honor Global Privacy Control (GPC) signals. When detected, all non-essential analytics are disabled for your session.
4.4 Children's Privacy (COPPA): The Service is NOT directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such data, contact ${CONTACT_EMAIL} immediately.
4.5 Insights Decision-Making: If an Insights Output is used to inform a significant decision affecting you, you have the right to request human review. Contact ${CONTACT_EMAIL}.

SECTION 5 — DATA SECURITY
5.1 Encryption: All data is encrypted in transit (TLS 1.3) and at rest (256-bit AES) within Google Cloud.
5.2 Access Controls: Role-based access control (RBAC) via Firebase Authentication. Multi-factor authentication available.
5.3 Breach Notification: In the event of a security breach affecting your personal information, we will notify you as required by 73 P.S. § 2303 and applicable federal law.

SECTION 6 — DATA RETENTION
Agreement records are retained for 7 years following account closure for legal compliance. Operational data is purged 30 days after subscription cancellation per our Cancellation Policy.

SECTION 7 — CONTACT
Data Privacy Inquiries: ${CONTACT_EMAIL}
Mailing: IntelOptics Insights, Pennsylvania, USA

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PART 3 — INSIGHTS-GENERATED CONTENT DISCLAIMER
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Effective Date: ${EFFECTIVE_DATE}

${COMPANY_NAME} operates as an Insights-assisted intelligence platform. The following disclaimers apply in accordance with the Federal Trade Commission Act § 5 (prohibiting unfair or deceptive acts), the FTC's Insights Guidance (2024), and the framework of the Uniform Artificial Intelligence Act (UAAA), and applicable emerging U.S. Insights transparency requirements.

1. INSIGHTS TRANSPARENCY NOTICE
The platform uses proprietary "Insights Engines" (Google Gemini Enterprise) to generate intelligence reports, risk analyses, and operational plans. You are hereby notified that you are interacting with Insights-generated content.

2. ACCURACY LIMITATIONS & "HALLUCINATIONS"
Insights language models can and do produce inaccurate, incomplete, or fabricated information ("hallucinations") — confident-sounding statements that are factually incorrect. The probability of hallucinations increases with: (a) highly specific factual claims; (b) real-time geopolitical situations; (c) queries outside the model's training data. ALL OUTPUTS MUST BE INDEPENDENTLY VERIFIED BY A QUALIFIED HUMAN PROFESSIONAL BEFORE BEING USED FOR ANY OPERATIONAL, TACTICAL, STRATEGIC, LEGAL, OR LIFE-SAFETY PURPOSE.

3. NOT PROFESSIONAL ADVICE
Outputs do NOT constitute: professional security advice, legal advice, medical advice, financial advice, intelligence-grade analysis, or any form of licensed professional counsel. ${COMPANY_NAME} is an informational support tool, not a substitute for licensed experts.

4. HUMAN-IN-THE-LOOP (HITL) REQUIREMENT
Consistent with the FTC's guidance on Insights accountability and the UAAA's human oversight provisions, Subscriber expressly acknowledges the MANDATORY requirement for human review of all Outputs before: (a) deployment on any mission; (b) submission to external parties; (c) use in any decision affecting personnel safety or organizational resources.

5. OSINT & DYNAMIC DATA LIMITATIONS
Analysis incorporates Open-Source Intelligence (OSINT) and third-party public data. We do not guarantee the accuracy or currency of these sources. Geopolitical situations change rapidly; information may become obsolete immediately after generation.

6. INSIGHTS BIAS NOTICE
Insights models may reflect biases present in training data. Outputs may contain unintentional cultural, political, or geographic biases. Subscriber agrees to critically evaluate all Outputs for potential bias before use.

7. NO REAL-TIME CAPABILITY
Unless explicitly marked as "Live Feed," platform Outputs are based on the Insights model's knowledge cutoff date and may not reflect current events.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PART 4 — DATA PROCESSING ADDENDUM (DPA)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${COMPANY_NAME} processes data as follows:

PROCESSOR RELATIONSHIPS:
• Google LLC (Firebase / Gemini API) — Infrastructure & Insights Processing. Google Data Processing Agreement applies.
• Paddle.com Market Ltd — Payment Processing. Paddle acts as Merchant of Record.
• Mapbox — Geographic visualization data (no personal data shared).

DATA CATEGORIES PROCESSED:
• Account identifiers (email, UID, organization ID)
• Usage telemetry (anonymized)
• Legal agreement records (name, timestamp, checkbox status)
• Insights query inputs (transient — not stored)

PROHIBITED DATA TYPES (you must NOT submit):
• Social Security Numbers or government ID numbers
• Financial account numbers or card data
• Protected Health Information (PHI) under HIPAA
• Biometric identifiers
• Information about persons under 13 years of age
• Classified or government-restricted information
• Data subject to Export Administration Regulations (EAR) or ITAR

${tier === 'business' ? `BUSINESS TIER ADDITIONAL PROVISIONS:
As a Business tier subscriber, you are responsible for ensuring that all users you provision within your organization have acknowledged and agreed to this Agreement. By adding users to your organization, you represent that they have consented to the data processing described herein.\n` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PART 5 — REFUND & CANCELLATION POLICY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

JURISDICTION: This policy applies to subscribers located in the United States of America. By subscribing, you confirm you have read and agreed to these terms. All payments are processed by Paddle.com Market Ltd (Merchant of Record) in accordance with their Terms of Service and applicable U.S. law.

NO REFUND POLICY
Subscription fees paid by U.S.-based subscribers are NON-REFUNDABLE once payment has been successfully processed. By completing payment, you acknowledge and agree that no monetary refunds will be issued, regardless of usage, satisfaction, or the reason for cancellation. This applies to both monthly and annual billing cycles. This no-refund policy is enforceable under Pennsylvania contract law and the E-SIGN Act, as you have acknowledged it by electronic signature during the signup process.

EXCEPTIONS: In the event of a billing error, duplicate charge, or technical failure attributable solely to ${COMPANY_LEGAL} or Paddle that results in an unauthorized charge, a refund may be considered at Company's sole discretion. To report such an error, contact ${CONTACT_EMAIL} with your transaction ID within 7 days of the charge.

MONTHLY SUBSCRIBERS — CANCELLATION TERMS
Monthly subscribers may cancel their subscription through Billing & Invoices. Upon cancellation:
• Your subscription will not renew at the end of the current monthly billing period.
• You will retain full access to all platform features through the end of your current monthly billing cycle.
• No partial refunds will be issued for unused days within the billing period.
• Access is permanently deactivated at the end of the final paid billing period.

ANNUAL SUBSCRIBERS — CANCELLATION TERMS
Annual subscribers may cancel their subscription through Billing & Invoices. Upon cancellation:
• Your subscription will not renew at the end of the current annual billing period.
• You will retain full access to all platform features through the end of your paid annual term (the full 12-month period from your subscription start date).
• No partial or pro-rata refunds will be issued for unused months within the annual term.
• Access is permanently deactivated at the end of the paid annual term.
• This means annual subscribers who cancel mid-year continue to receive the full value of the subscription they purchased — access through the date their annual term concludes.

DATA AFTER CANCELLATION
Organization data is vaulted for 30 days following the final access date. Re-subscribing within 30 days restores all data. After 30 days, all operational data is permanently deleted and cannot be recovered. Legal agreement records are retained for 7 years per applicable law.

CHARGEBACKS & DISPUTES
Initiating a payment chargeback, dispute, or reversal through your bank or card provider for a valid subscription charge is a violation of this Agreement and may result in: (a) immediate permanent account suspension; (b) referral to collections; (c) legal action to recover fees plus costs. Please contact ${CONTACT_EMAIL} to resolve any billing concerns before initiating a chargeback.

NO CANCELLATION FEES
There are no fees, penalties, or charges for cancelling your subscription. Cancellation only stops future renewal — it does not trigger any additional billing.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ELECTRONIC SIGNATURE NOTICE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

By typing your full legal name below and clicking "Proceed to Payment," you are providing a legally binding electronic signature in accordance with the Electronic Signatures in Global and National Commerce Act (E-SIGN Act, 15 U.S.C. § 7001 et seq.) and the Uniform Electronic Transactions Act (UETA), as adopted by Pennsylvania (73 P.S. § 2260.101 et seq.). Your electronic signature has the same legal effect as a handwritten signature.

You confirm that:
(1) You have read and understand all documents above in their entirety.
(2) You have had the opportunity to consult with legal counsel.
(3) You are at least 18 years of age.
(4) You have the legal authority to enter into this Agreement.
(5) You intend to be legally bound by this Agreement.

For questions, contact: ${CONTACT_EMAIL}
${COMPANY_LEGAL} | ${GOVERNING_STATE}, USA
    `.trim();
}

// ─── Simple hash for agreement version tracking ────────────────────────────────
function simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
}

// ─── Component ─────────────────────────────────────────────────────────────────
const SubscriberAgreementModal: React.FC<SubscriberAgreementModalProps> = ({
    tier,
    userEmail,
    userUid,
    billingLabel,
    onAccepted,
    onDeclined,
}) => {
    const [step, setStep] = useState<'read' | 'sign'>('read');
    const [scrollProgress, setScrollProgress] = useState(0);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [scrollCompletedAt, setScrollCompletedAt] = useState<string | null>(null);
    const [signatureName, setSignatureName] = useState('');
    const [pdfDownloaded, setPdfDownloaded] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Section tracking
    const sections = ['Subscriber Agreement', 'Privacy Policy', 'Insights Disclaimer', 'Data Processing', 'Refund Policy'];
    const [activeSectionIdx, setActiveSectionIdx] = useState(0);

    // Checkboxes
    const [cbMain, setCbMain] = useState(false);
    const [cbLiability, setCbLiability] = useState(false);
    const [cbAuthority, setCbAuthority] = useState(false);

    const scrollRef = useRef<HTMLDivElement>(null);
    const signatureRef = useRef<HTMLInputElement>(null);
    const now = new Date();
    const signedAtDisplay = formatLocalDateTime(now);

    const agreementText = buildAgreementText(tier, billingLabel);
    const agreementHash = simpleHash(agreementText);

    const isBusiness = tier === 'business';
    const allCheckboxesMet = isBusiness
        ? (cbMain && cbLiability && cbAuthority)
        : cbMain;
    const canProceed = allCheckboxesMet && signatureName.trim().length >= 4;

    // ── Scroll tracking ────────────────────────────────────────────────────────
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const pct = Math.min(100, Math.round((scrollTop / (scrollHeight - clientHeight)) * 100));
        setScrollProgress(pct);

        // Update active section based on scroll position
        const sectionEls = el.querySelectorAll('[data-section]');
        let current = 0;
        sectionEls.forEach((sec, i) => {
            const rect = sec.getBoundingClientRect();
            const containerRect = el.getBoundingClientRect();
            if (rect.top - containerRect.top < containerRect.height / 2) {
                current = i;
            }
        });
        setActiveSectionIdx(current);

        if (pct >= 98 && !hasScrolledToBottom) {
            setHasScrolledToBottom(true);
            setScrollCompletedAt(new Date().toISOString());
        }
    }, [hasScrolledToBottom]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // ── PDF Generation ─────────────────────────────────────────────────────────
    const handleDownloadPdf = async () => {
        setIsGeneratingPdf(true);
        try {
            // Dynamically import jsPDF to avoid bundle bloat if not needed
            const { default: jsPDF } = await import('jspdf');
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 18;
            const maxWidth = pageWidth - margin * 2;
            let y = margin;

            const addText = (text: string, size: number, bold = false, color: [number, number, number] = [30, 30, 30]) => {
                doc.setFontSize(size);
                doc.setFont('helvetica', bold ? 'bold' : 'normal');
                doc.setTextColor(...color);
                const lines = doc.splitTextToSize(text, maxWidth);
                lines.forEach((line: string) => {
                    if (y > 270) { doc.addPage(); y = margin; }
                    doc.text(line, margin, y);
                    y += size * 0.45;
                });
                y += 2;
            };

            // Header
            addText('INTELOPTICS INSIGHTS', 16, true, [10, 60, 150]);
            addText(`EXECUTED SUBSCRIBER AGREEMENT`, 11, true, [10, 60, 150]);
            addText(`Version: ${AGREEMENT_VERSION}  |  Plan: ${billingLabel}`, 8, false, [100, 100, 100]);
            y += 4;
            doc.setDrawColor(10, 60, 150);
            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // Agreement text
            const textLines = agreementText.split('\n');
            textLines.forEach(line => {
                if (line.startsWith('━')) {
                    y += 2;
                    doc.setDrawColor(180, 180, 180);
                    doc.line(margin, y, pageWidth - margin, y);
                    y += 4;
                } else if (line.startsWith('PART ') || line.startsWith('ARTICLE ') || line.startsWith('SECTION ')) {
                    addText(line, 9, true);
                } else {
                    addText(line || ' ', 8);
                }
            });

            // Signature block
            y += 8;
            if (y > 240) { doc.addPage(); y = margin; }
            doc.setDrawColor(10, 60, 150);
            doc.line(margin, y, pageWidth - margin, y);
            y += 6;
            addText('DIGITAL SIGNATURE RECORD', 10, true, [10, 60, 150]);
            y += 2;
            addText(`Subscriber Name (Digital Signature): ${signatureName || '[Not yet signed]'}`, 9, false);
            addText(`Email: ${userEmail}`, 9, false);
            addText(`User ID: ${userUid}`, 9, false);
            addText(`Signed: ${signedAtDisplay}`, 9, false);
            addText(`Agreement Version: ${AGREEMENT_VERSION}`, 9, false);
            addText(`Agreement Hash: ${agreementHash}`, 9, false);
            addText(`Scroll Completed: ${scrollCompletedAt || 'N/A'}`, 9, false);
            addText(`Checkboxes: Main Agreement: ${cbMain ? 'YES' : 'NO'}${isBusiness ? ` | Liability: ${cbLiability ? 'YES' : 'NO'} | Authority: ${cbAuthority ? 'YES' : 'NO'}` : ''}`, 9, false);
            y += 6;
            addText('This document was electronically signed pursuant to the E-SIGN Act (15 U.S.C. § 7001) and PA UETA (73 P.S. § 2260.101). This constitutes a legally binding agreement.', 7, false, [100, 100, 100]);
            y += 4;
            addText(`© ${new Date().getFullYear()} ${COMPANY_LEGAL}. All rights reserved. Governing Law: ${GOVERNING_STATE}.`, 7, false, [150, 150, 150]);

            // Watermark on each page
            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(48);
                doc.setTextColor(240, 240, 240);
                doc.setFont('helvetica', 'bold');
                doc.text('EXECUTED COPY', pageWidth / 2, 148, { angle: 45, align: 'center' });
            }

            doc.save(`IntelOptics-Subscriber-Agreement-${tier.toUpperCase()}-${localDateInputValue()}.pdf`);
            setPdfDownloaded(true);
        } catch (err) {
            console.error('PDF generation failed:', err);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    // ── Final acceptance ───────────────────────────────────────────────────────
    const handleAccept = () => {
        if (!canProceed) return;
        setIsSubmitting(true);
        const record: AgreementRecord = {
            version: AGREEMENT_VERSION,
            agreementHash,
            tier,
            acknowledgedAt: new Date().toISOString(),
            scrollCompletedAt: scrollCompletedAt || new Date().toISOString(),
            signatureName: signatureName.trim(),
            acknowledgedByEmail: userEmail,
            acknowledgedByUid: userUid,
            planLabel: billingLabel,
            checkboxes: {
                mainAgreement: cbMain,
                liabilityAcknowledgement: cbLiability,
                authorityConfirmation: cbAuthority,
            },
            pdfDownloaded,
            userAgent: navigator.userAgent.slice(0, 200),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        onAccepted(record);
    };

    // ── Section labels for agreement text ────────────────────────────────────
    const sectionMarkers = [
        'PART 1', 'PART 2', 'PART 3', 'PART 4', 'PART 5'
    ];

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4">
            <div className="relative w-full max-w-4xl max-h-[96vh] bg-[#0a1020] border border-blue-900/40 rounded-2xl shadow-2xl flex flex-col overflow-hidden">

                {/* ── Header ── */}
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
                    <div>
                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-0.5">Legally Binding Agreement</p>
                        <h2 className="text-lg font-black text-white">IntelOptics Subscriber Agreement</h2>
                        <p className="text-xs text-slate-400 mt-0.5">{billingLabel} · {AGREEMENT_VERSION}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Step indicators */}
                        <div className="hidden sm:flex items-center gap-2">
                            {['1. Read', '2. Sign'].map((label, i) => (
                                <div key={label} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                                    (i === 0 && step === 'read') || (i === 1 && step === 'sign')
                                        ? 'bg-blue-600 text-white'
                                        : i === 0 && step === 'sign'
                                        ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                                        : 'bg-white/5 text-slate-500'
                                }`}>
                                    {i === 0 && step === 'sign' ? '✓' : null}
                                    {label}
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={onDeclined}
                            className="text-slate-500 hover:text-white transition-colors p-1"
                            title="Close (you will not be able to subscribe without agreeing)"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* ── STEP 1: Read ─────────────────────────────────────────────── */}
                {step === 'read' && (
                    <div className="flex flex-1 overflow-hidden">
                        {/* Left: Section nav */}
                        <div className="hidden md:flex flex-col w-44 shrink-0 border-r border-white/10 p-3 gap-1">
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Documents</p>
                            {sections.map((sec, i) => (
                                <button
                                    key={sec}
                                    onClick={() => {
                                        const el = scrollRef.current;
                                        if (!el) return;
                                        const marker = el.querySelector(`[data-section="${i}"]`);
                                        marker?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }}
                                    className={`text-left text-xs font-medium px-2 py-1.5 rounded-lg transition-colors ${
                                        activeSectionIdx === i
                                            ? 'bg-blue-600/20 text-blue-300'
                                            : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                                    }`}
                                >
                                    <span className="text-[9px] font-black text-slate-600 mr-1">{i + 1}.</span>
                                    {sec}
                                </button>
                            ))}
                            {/* Progress */}
                            <div className="mt-auto pt-4 border-t border-white/10">
                                <p className="text-[9px] text-slate-500 font-bold mb-2">Read Progress</p>
                                <div className="w-full bg-white/10 rounded-full h-1.5">
                                    <div
                                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: `${scrollProgress}%` }}
                                    />
                                </div>
                                <p className="text-[10px] text-slate-400 mt-1 font-bold">{scrollProgress}%</p>
                                {hasScrolledToBottom && (
                                    <p className="text-[9px] text-green-400 font-bold mt-1">✓ Fully read</p>
                                )}
                            </div>
                        </div>

                        {/* Right: Scrollable document */}
                        <div className="flex flex-col flex-1 overflow-hidden">
                            {/* Mobile progress bar */}
                            <div className="md:hidden px-4 pt-3 pb-1">
                                <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                    <span>Read Progress</span>
                                    <span>{scrollProgress}%</span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-1">
                                    <div className="bg-blue-500 h-1 rounded-full transition-all" style={{ width: `${scrollProgress}%` }} />
                                </div>
                            </div>

                            {/* Document text */}
                            <div
                                ref={scrollRef}
                                className="flex-1 overflow-y-auto p-5 font-mono text-[11px] leading-relaxed text-slate-300 space-y-0"
                                style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 transparent' }}
                            >
                                {agreementText.split('\n').map((line, idx) => {
                                    // Section markers for nav
                                    const secIdx = sectionMarkers.findIndex(m => line.startsWith(m));
                                    if (line.startsWith('━')) {
                                        return <div key={idx} className="border-t border-slate-700 my-4" />;
                                    }
                                    if (secIdx >= 0) {
                                        return (
                                            <div key={idx} data-section={secIdx} className="text-blue-400 font-black text-xs mt-6 mb-2 tracking-wide">
                                                {line}
                                            </div>
                                        );
                                    }
                                    if (/^(ARTICLE|SECTION) \d/.test(line)) {
                                        return <p key={idx} className="text-white font-bold text-[11px] mt-3 mb-1">{line}</p>;
                                    }
                                    if (/^\d+\.\d+/.test(line)) {
                                        return <p key={idx} className="text-slate-200 font-semibold text-[11px] mt-2 mb-0.5">{line}</p>;
                                    }
                                    if (line.startsWith('•')) {
                                        return <p key={idx} className="text-slate-400 pl-4 text-[11px]">{line}</p>;
                                    }
                                    if (!line.trim()) {
                                        return <div key={idx} className="h-2" />;
                                    }
                                    // ALL CAPS lines = important notices
                                    if (line === line.toUpperCase() && line.trim().length > 20 && !/^[0-9]/.test(line)) {
                                        return <p key={idx} className="text-amber-400/80 font-bold text-[10px] my-2 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1">{line}</p>;
                                    }
                                    return <p key={idx} className="text-slate-400 text-[11px]">{line}</p>;
                                })}

                                {/* Spacer so user can confirm they're at the true bottom */}
                                <div className="h-8 flex items-center justify-center">
                                    {hasScrolledToBottom
                                        ? <span className="text-xs text-green-400 font-bold">✓ You have read this agreement in full</span>
                                        : <span className="text-xs text-slate-600">Scroll to the bottom to continue</span>
                                    }
                                </div>
                            </div>

                            {/* Continue button */}
                            <div className="px-5 py-4 border-t border-white/10 bg-[#0a1020] flex items-center justify-between gap-4 shrink-0">
                                <p className="text-xs text-slate-500">
                                    {hasScrolledToBottom
                                        ? '✓ Agreement read — proceed to sign'
                                        : `Scroll to read the full agreement (${scrollProgress}%)`
                                    }
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={onDeclined}
                                        className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors"
                                    >
                                        Decline
                                    </button>
                                    <button
                                        onClick={() => setStep('sign')}
                                        disabled={!hasScrolledToBottom}
                                        className="px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs rounded-lg transition-all"
                                    >
                                        Continue to Sign →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── STEP 2: Sign ─────────────────────────────────────────────── */}
                {step === 'sign' && (
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="max-w-2xl mx-auto space-y-6">

                            {/* Plan summary */}
                            <div className="bg-blue-600/10 border border-blue-500/20 rounded-xl p-4">
                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Subscribing To</p>
                                <p className="text-white font-bold text-lg">{billingLabel}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{AGREEMENT_VERSION} · {EFFECTIVE_DATE}</p>
                            </div>

                            {/* Signature field */}
                            <div>
                                <label className="text-xs font-black text-slate-300 uppercase tracking-widest block mb-2">
                                    Digital Signature — Type Your Full Legal Name
                                </label>
                                <p className="text-[10px] text-slate-500 mb-3">
                                    By typing your name, you provide a legally binding electronic signature under the E-SIGN Act (15 U.S.C. § 7001) and Pennsylvania UETA (73 P.S. § 2260.101).
                                </p>
                                <input
                                    ref={signatureRef}
                                    type="text"
                                    value={signatureName}
                                    onChange={e => setSignatureName(e.target.value)}
                                    placeholder="Type your full legal name..."
                                    className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors placeholder-slate-600"
                                />
                                {/* Signature preview */}
                                {signatureName.length >= 2 && (
                                    <div className="mt-3 p-4 bg-white/[0.03] border border-white/10 rounded-xl">
                                        <p className="text-[9px] text-slate-500 uppercase tracking-widest mb-2">Signature Preview</p>
                                        <p
                                            className="text-2xl text-blue-300"
                                            style={{ fontFamily: '"Dancing Script", cursive', letterSpacing: '0.02em' }}
                                        >
                                            {signatureName}
                                        </p>
                                        <div className="mt-2 border-t border-slate-700/50 pt-2 flex justify-between text-[9px] text-slate-500">
                                            <span>{userEmail}</span>
                                            <span>{signedAtDisplay}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Date/time — read only */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Date & Time (Auto)</label>
                                    <div className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-400 font-mono">
                                        {signedAtDisplay}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Agreement Version</label>
                                    <div className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-400 font-mono">
                                        {AGREEMENT_VERSION}
                                    </div>
                                </div>
                            </div>

                            {/* Checkboxes */}
                            <div className="space-y-3">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Required Acknowledgements</p>

                                {/* Always required */}
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <div className="mt-0.5 shrink-0">
                                        <input type="checkbox" checked={cbMain} onChange={e => setCbMain(e.target.checked)} className="sr-only" />
                                        <div className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${cbMain ? 'bg-blue-600 border-blue-600' : 'border-slate-600 group-hover:border-blue-500'}`}>
                                            {cbMain && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        I have read, understood, and agree to the <strong className="text-white">IntelOptics Subscriber Agreement</strong>, <strong className="text-white">Privacy Policy</strong>, <strong className="text-white">Insights-Generated Content Disclaimer</strong>, <strong className="text-white">Data Processing Addendum</strong>, <strong className="text-white">Acceptable Use Policy</strong>, and <strong className="text-white">Refund & Cancellation Policy</strong> in their entirety.
                                    </p>
                                </label>

                                {/* Business only */}
                                {isBusiness && (
                                    <>
                                        <label className="flex items-start gap-3 cursor-pointer group">
                                            <div className="mt-0.5 shrink-0">
                                                <input type="checkbox" checked={cbLiability} onChange={e => setCbLiability(e.target.checked)} className="sr-only" />
                                                <div className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${cbLiability ? 'bg-amber-600 border-amber-600' : 'border-slate-600 group-hover:border-amber-500'}`}>
                                                    {cbLiability && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                                                </div>
                                            </div>
                                            <p className="text-xs text-slate-300 leading-relaxed">
                                                <strong className="text-amber-400">AI Liability Acknowledgement:</strong> I acknowledge that AI-generated intelligence outputs are for professional informational use only. I accept sole responsibility for any operational, tactical, or strategic decisions made based on these outputs. I confirm that I will apply Human-in-the-Loop verification before any mission-critical application. IntelOptics Insights is not liable for any loss, damage, injury, or liability resulting from my reliance on platform outputs.
                                            </p>
                                        </label>

                                        <label className="flex items-start gap-3 cursor-pointer group">
                                            <div className="mt-0.5 shrink-0">
                                                <input type="checkbox" checked={cbAuthority} onChange={e => setCbAuthority(e.target.checked)} className="sr-only" />
                                                <div className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${cbAuthority ? 'bg-amber-600 border-amber-600' : 'border-slate-600 group-hover:border-amber-500'}`}>
                                                    {cbAuthority && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                                                </div>
                                            </div>
                                            <p className="text-xs text-slate-300 leading-relaxed">
                                                <strong className="text-amber-400">Organizational Authority:</strong> I confirm I am duly authorized to enter into this Agreement on behalf of my organization. I acknowledge that all users I provision within the platform are bound by this Agreement and the Acceptable Use Policy, and that my organization accepts full responsibility for their compliance.
                                            </p>
                                        </label>
                                    </>
                                )}
                            </div>

                            {/* PDF Download */}
                            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold text-slate-300">Download Your Signed Agreement</p>
                                    <p className="text-[10px] text-slate-500 mt-0.5">PDF includes all documents, your signature, and a timestamped audit record.</p>
                                </div>
                                <button
                                    onClick={handleDownloadPdf}
                                    disabled={isGeneratingPdf}
                                    className="shrink-0 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white font-bold text-xs rounded-lg transition-all border border-white/10"
                                >
                                    {isGeneratingPdf ? (
                                        <>
                                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                            {pdfDownloaded ? '✓ Downloaded' : 'Download PDF'}
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* E-SIGN Notice */}
                            <div className="text-[10px] text-slate-600 leading-relaxed bg-white/[0.02] rounded-lg p-3 border border-white/5">
                                Your typed name constitutes a legally binding electronic signature under the E-SIGN Act (15 U.S.C. § 7001), Pennsylvania UETA (73 P.S. § 2260.101), and applicable contract law. A complete audit record including your name, email, timestamp, agreement version hash, and checkbox confirmations will be securely stored in your account for 7 years. Governing law: {GOVERNING_STATE}.
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Footer / Action buttons (step 2 only) ──────────────────── */}
                {step === 'sign' && (
                    <div className="px-6 py-4 border-t border-white/10 bg-[#0a1020] flex items-center justify-between gap-4 shrink-0">
                        <button
                            onClick={() => setStep('read')}
                            className="text-xs font-bold text-slate-400 hover:text-white transition-colors"
                        >
                            ← Review Agreement
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={onDeclined}
                                className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white border border-white/10 rounded-lg transition-colors"
                            >
                                Decline
                            </button>
                            <button
                                onClick={handleAccept}
                                disabled={!canProceed || isSubmitting}
                                className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-all shadow-lg shadow-blue-900/40"
                            >
                                {isSubmitting ? 'Processing...' : 'Proceed to Payment →'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriberAgreementModal;
